/**
 * Voice allocation and lifetime.
 *
 * Every one-shot in the game runs through a pooled voice. A voice owns the
 * fixed part of the chain — level, distance filter, pan and reverb send — which
 * is built once and never rebuilt; the caller only supplies the oscillators and
 * buffer sources that make the actual noise. Those are registered with `own()`
 * so a stolen voice can silence them instead of leaving them running.
 *
 * There is a hard cap on concurrent voices and the cheapest one is stolen first,
 * so a bad frame (a hound screaming in a room full of dripping pipes) costs a
 * dropped drip rather than a dropped frame.
 */

import type { Vec2 } from '../core/types';
import { evalSpatial, makeSpatial, type Listener, type Spatial } from './spatial';

/**
 * Voice classes, cheapest first. Stealing walks up from `ambient`: a growl may
 * take a drip's voice, a drip may never take a growl's.
 */
export const PRIORITY = {
  /** Drips, distant metal, room detail. */
  ambient: 0,
  /** The player's own footsteps. */
  step: 1,
  /** Pickups, doors, world events. */
  world: 2,
  /** Anything an entity makes. */
  entity: 3,
  /** Menu clicks, damage, death. Never stolen. */
  ui: 4,
} as const;

export interface Voice {
  /** Build the synthesis chain into this. Its gain is owned by the pool. */
  readonly input: GainNode;
  /** Register a source so the pool can stop it if the voice is stolen. */
  own<T extends AudioScheduledSourceNode>(node: T): T;
  /** Push the auto-release out to `time` (context clock). */
  keepUntil(time: number): void;
}

export interface VoiceOptions {
  priority: number;
  /** Seconds after which the pool tears the voice down. */
  duration: number;
  /** Linear level, before distance attenuation. */
  gain?: number;
  /** Base reverb send, 0..1, scaled by distance. */
  send?: number;
  /** World position. Omitted means "at the listener". */
  pos?: Vec2 | null;
  /** Re-evaluate position every few frames. Only worth it for long sounds. */
  track?: boolean;
  /** Extra low-pass on top of the distance filter, Hz. */
  cutoff?: number;
}

class PooledVoice implements Voice {
  /** Rebuilt per acquisition so a stolen voice's tail can fade out in peace. */
  input: GainNode;
  readonly lp: BiquadFilterNode;
  readonly panner: StereoPannerNode;
  readonly send: GainNode;
  readonly sources: AudioScheduledSourceNode[] = [];

  active = false;
  priority = 0;
  endsAt = 0;
  positional = false;
  track = false;
  px = 0;
  pz = 0;
  baseGain = 1;
  baseSend = 0;
  cutoff = 20000;

  constructor(ctx: AudioContext, dry: AudioNode, reverbIn: AudioNode) {
    this.lp = ctx.createBiquadFilter();
    this.lp.type = 'lowpass';
    this.lp.frequency.value = 20000;
    this.lp.Q.value = 0.4;

    this.panner = ctx.createStereoPanner();
    this.send = ctx.createGain();
    this.send.gain.value = 0;

    this.lp.connect(this.panner);
    this.panner.connect(dry);
    this.panner.connect(this.send);
    this.send.connect(reverbIn);

    this.input = ctx.createGain();
  }

  own<T extends AudioScheduledSourceNode>(node: T): T {
    this.sources.push(node);
    return node;
  }

  keepUntil(time: number): void {
    if (time > this.endsAt) this.endsAt = time;
  }
}

export class VoicePool {
  private readonly voices: PooledVoice[] = [];
  /** Inputs of stolen voices, waiting for their fade-out to finish. */
  private readonly retiring: Array<{ node: GainNode; at: number }> = [];
  private readonly scratch: Spatial = makeSpatial();
  private frame = 0;

  constructor(
    private readonly ctx: AudioContext,
    private readonly dry: AudioNode,
    private readonly reverbIn: AudioNode,
    private readonly listener: Listener,
    private readonly cap = 24,
  ) {}

  /** Returns null when nothing cheap enough could be freed. */
  acquire(o: VoiceOptions): Voice | null {
    // Sounds are played before `update` runs, and after a tab-out no sweep has
    // happened at all, so reclaim finished voices here rather than stealing
    // live ones from a pool that is only nominally full.
    this.sweepExpired();

    let v: PooledVoice | null = null;
    for (const c of this.voices) {
      if (!c.active) {
        v = c;
        break;
      }
    }
    if (!v) {
      if (this.voices.length < this.cap) {
        v = new PooledVoice(this.ctx, this.dry, this.reverbIn);
        this.voices.push(v);
      } else {
        v = this.steal(o.priority);
        if (!v) return null;
      }
    }

    const t = this.ctx.currentTime;
    v.active = true;
    v.priority = o.priority;
    v.endsAt = t + Math.max(0.02, o.duration);
    v.track = o.track === true;
    v.baseGain = o.gain ?? 1;
    v.baseSend = o.send ?? 0.16;
    v.cutoff = o.cutoff ?? 20000;
    v.positional = !!o.pos;
    if (o.pos) {
      v.px = o.pos.x;
      v.pz = o.pos.z;
    }

    v.input = this.ctx.createGain();
    v.input.connect(v.lp);
    this.apply(v, t, false);
    return v;
  }

  /** Sweep finished voices and follow the ones that track the listener. */
  update(): void {
    const now = this.ctx.currentTime;
    this.frame++;
    // Tracking at 15Hz is indistinguishable from per-frame and costs a quarter
    // as much parameter automation.
    const doTrack = (this.frame & 3) === 0;

    for (const v of this.voices) {
      if (!v.active) continue;
      if (now >= v.endsAt) {
        this.retire(v, 0);
        continue;
      }
      if (v.track && v.positional && doTrack) this.apply(v, now, true);
    }

    for (let i = this.retiring.length - 1; i >= 0; i--) {
      const r = this.retiring[i]!;
      if (now >= r.at) {
        r.node.disconnect();
        this.retiring.splice(i, 1);
      }
    }
  }

  private sweepExpired(): void {
    const now = this.ctx.currentTime;
    for (const v of this.voices) if (v.active && now >= v.endsAt) this.retire(v, 0);
  }

  private steal(priority: number): PooledVoice | null {
    let best: PooledVoice | null = null;
    for (const c of this.voices) {
      if (c.priority > priority) continue;
      if (!best || c.priority < best.priority || (c.priority === best.priority && c.endsAt < best.endsAt)) best = c;
    }
    if (!best) return null;
    this.retire(best, 0.025);
    return best;
  }

  private retire(v: PooledVoice, fade: number): void {
    v.active = false;
    const t = this.ctx.currentTime;
    if (fade > 0) {
      const g = v.input.gain;
      g.cancelScheduledValues(t);
      g.setValueAtTime(g.value, t);
      g.linearRampToValueAtTime(0, t + fade);
      for (const s of v.sources) stopSafely(s, t + fade);
      this.retiring.push({ node: v.input, at: t + fade + 0.05 });
    } else {
      // Cutting the chain at both ends leaves an island nothing references, so
      // the whole burst is collectable now rather than whenever the engine
      // gets around to noticing the sources have finished.
      for (const s of v.sources) {
        stopSafely(s, t);
        s.disconnect();
      }
      v.input.disconnect();
    }
    v.sources.length = 0;
  }

  private apply(v: PooledVoice, t: number, smooth: boolean): void {
    let gain = v.baseGain;
    let pan = 0;
    let cutoff = v.cutoff;
    let send = v.baseSend;

    if (v.positional) {
      const s = evalSpatial(this.listener, v.px, v.pz, this.scratch);
      gain *= s.gain;
      pan = s.pan;
      cutoff = Math.min(cutoff, s.cutoff);
      send = Math.min(1.5, send * s.sendScale);
    }

    if (smooth) {
      v.input.gain.setTargetAtTime(gain, t, 0.06);
      v.panner.pan.setTargetAtTime(pan, t, 0.06);
      v.lp.frequency.setTargetAtTime(cutoff, t, 0.1);
      v.send.gain.setTargetAtTime(send, t, 0.08);
    } else {
      // The shared nodes carry automation from the previous occupant.
      v.input.gain.setValueAtTime(gain, t);
      v.panner.pan.cancelScheduledValues(t);
      v.panner.pan.setValueAtTime(pan, t);
      v.lp.frequency.cancelScheduledValues(t);
      v.lp.frequency.setValueAtTime(cutoff, t);
      v.send.gain.cancelScheduledValues(t);
      v.send.gain.setValueAtTime(send, t);
    }
  }
}

/** `stop()` throws if the node was never started. Nothing here cares. */
function stopSafely(node: AudioScheduledSourceNode, when: number): void {
  try {
    node.stop(when);
  } catch {
    /* already finished or never started */
  }
}
